import type { Asset } from '../types';
import type { MarketQuote } from '../services/marketDataService';
import { buildQuoteProviderRequest } from './quoteRequest';

const normalize = (value?: string | null) => (value ? value.trim().toUpperCase() : undefined);

const extractSymbolAlias = (value?: string | null) => {
    const normalized = normalize(value);
    if (!normalized) return undefined;
    const withoutSource = normalized.includes(':') ? normalized.split(':').slice(1).join(':') : normalized;
    const [symbol, maybeExchange] = withoutSource.split('.');
    if (!symbol || !maybeExchange) return withoutSource;
    if (['US', 'LSE', 'L', 'LN'].includes(maybeExchange)) return symbol;
    return withoutSource;
};

type QuoteAssetLike = Pick<Asset, 'symbol' | 'instrumentId' | 'dataProviderId' | 'exchange' | 'priceCurrency' | 'quoteCurrency' | 'canonicalQuoteKey'>;

export const getQuoteLookupKeys = (asset: QuoteAssetLike): string[] => {
    const keys = new Set<string>();
    const add = (value?: string | null) => {
        const normalized = normalize(value);
        if (normalized) keys.add(normalized);
    };

    add(asset.canonicalQuoteKey);
    add(asset.instrumentId ? `instrument:${asset.instrumentId}` : undefined);
    add(asset.instrumentId);
    add(asset.dataProviderId);
    if (asset.dataProviderId && !asset.dataProviderId.includes(':')) {
        add(`eodhd:${asset.dataProviderId}`);
    }
    add(asset.symbol);

    const request = buildQuoteProviderRequest(asset);
    if (request) {
        add(`${request.source}:${request.providerValue}`);
        add(request.providerValue);
        add(extractSymbolAlias(request.providerValue));
    }

    add(extractSymbolAlias(asset.dataProviderId));
    return Array.from(keys);
};

export const getQuoteAliasKeysFromRequest = (requestKey: string, providerId?: string, providerKey?: string, symbol?: string, instrumentId?: string) => {
    const keys = new Set<string>();
    const add = (value?: string | null) => {
        const normalized = normalize(value);
        if (normalized) keys.add(normalized);
    };

    const [source, requestValueRaw] = requestKey.split(':', 2);
    const requestValue = requestValueRaw || requestKey;
    const normalizedProviderId = normalize(providerId);
    const symbolAlias = extractSymbolAlias(symbol) || extractSymbolAlias(providerId) || extractSymbolAlias(providerKey) || extractSymbolAlias(requestValue);

    add(requestKey);
    add(instrumentId);
    add(instrumentId ? `instrument:${instrumentId}` : undefined);
    add(providerId);
    add(providerKey);
    add(requestValue);
    add(extractSymbolAlias(requestKey));
    add(extractSymbolAlias(providerId));
    add(extractSymbolAlias(providerKey));

    if (normalizedProviderId && source) {
        add(`${source}:${normalizedProviderId}`);
    }

    if (symbolAlias) {
        add(symbolAlias);
        add(`legacy:${symbolAlias}`);
    }

    return Array.from(keys);
};

export const resolveLiveQuoteForAsset = (asset: QuoteAssetLike, liveQuotes: Record<string, MarketQuote>) => {
    for (const key of getQuoteLookupKeys(asset)) {
        const quote = liveQuotes[key];
        if (quote) return quote;
    }
    return undefined;
};
